/**
 * Curriculum Report Service
 *
 * Fetch API implementation of the curriculum reports service.
 * Migrated from: assets/js/services/reporting/curriculumReports.js
 */

import type { ReportDefinition } from '../../types/ReportDefinition';
import { fetchSessionJson } from '../SessionRequest';
import { toRequiredPositiveInteger } from '../Utilities';

const API_SERVER = process.env.API_SERVER || '';

export interface CurriculumReport extends ReportDefinition {
  report_id: string;
}

export interface ReportDataResponse {
  // Define based on actual API response structure
  [key: string]: unknown;
}

/**
 * Fetches curriculum reports, optionally limited to the selected class.
 * @returns Promise resolving to the array of reports
 */
export async function getReports(classId?: number | string): Promise<CurriculumReport[]> {
  const query = classId === undefined ? '' : `?classId=${toRequiredPositiveInteger(classId, 'classId')}`;
  return fetchSessionJson<CurriculumReport[]>(
    `${API_SERVER}/reports/curriculum/reports${query}`,
    'Failed to fetch reports'
  );
}

/**
 * Fetches report data for a specific report.
 * @param reportKey - The key identifying the report
 * @param filter - Optional filter parameter
 * @returns Promise resolving to the report data, or empty array if no reportKey
 */
export async function getReportData(
  reportKey: string | null | undefined,
  filter?: string
): Promise<ReportDataResponse | []> {
  if (!reportKey) {
    return [];
  }

  const params = new URLSearchParams();
  if (filter) {
    params.set('filter', filter);
  }

  const queryString = params.toString();
  const url = `${API_SERVER}/reports/curriculum/reports/${encodeURIComponent(reportKey)}${
    queryString ? `?${queryString}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch report data: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

const CurriculumReportService = {
  getReports,
  getReportData,
};

export default CurriculumReportService;
